/* istanbul ignore file - test e2e */

import { faArrowRotateLeft, faCloudExclamation, faRight, faWandMagicSparkles } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LoadingPage } from '@partful/features/instructions/ui/LoadingPage';
import { ModelViewer } from '@partful/features/instructions/ui/ModelViewer/ModelViewer';
import { WizardErrorPage } from '@partful/features/instructions/ui/WizardErrorPage';
import { useQueryParams } from '@partful/shared/lib/hooks/useQueryParams';
import { Button } from '@partful/shared/ui/Button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogPanel } from '@partful/shared/ui/Dialog/Dialog';
import { Typography } from '@partful/shared/ui/Typography';
import { useTenant } from '@providers/Tenant';
import { Configuration, patchCADJob, patchConfiguration } from '@services/productInventory';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect } from 'react-router';
import { Link } from 'react-router-dom';
import { useModel } from '../../viewer/lib/useModel';
import styles from './PreviewPage.module.scss';

export function PreviewPage() {
  const { t: i18n } = useTranslation();
  const { accountId } = useTenant();
  const { productName, runID, configurationPID } = useQueryParams();

  const [swapYZAxis, setSwapYZAxis] = useState<boolean>(false);
  const [swapFacing, setSwapFacing] = useState<boolean>(false);
  const [confirmChanges, setConfirmChanges] = useState(false);

  /* Load 3D model */
  const [{ data: model, fetching: isLoadingModel, error: isErrorLoadingModel }] = useModel({
    account: accountId,
    product: productName,
  });

  const {
    isLoading: isPatchJobLoading,
    isError: isPatchJobError,
    isSuccess: isPatchJobSuccess,
    mutateAsync: patchCADAsyncMutation,
  } = useMutation(async () => {
    if (runID !== null) {
      await patchCADJob(runID, accountId, { status: 'IN_PROGRESS_REQUIRES_ATTRIBUTE_MAPPING' });
    }
  });

  const {
    isLoading: isPatchConfigLoading,
    isError: isPatchConfigError,
    isSuccess: isPatchConfigSuccess,
    mutate: patchConfigurationAsyncMutation,
  } = useMutation(async (data: Partial<Configuration>) => {
    await patchConfiguration(configurationPID, data, accountId);
  });

  const handleSubmit = async () => {
    try {
      await patchCADAsyncMutation();
      patchConfigurationAsyncMutation({
        axisOrientation: swapYZAxis ? 'Z-Up' : 'Y-Up',
        handedness: swapFacing ? 'LHS' : 'RHS',
      });
    } catch (e) {
      // don't need to explicitly handle this as the useMutation will handle it instead
    }
  };

  if (isPatchJobSuccess && isPatchConfigSuccess) {
    return <Redirect to={`/instructions/manage/${configurationPID}`} />;
  }

  if (isPatchJobLoading || isPatchConfigLoading || isLoadingModel) {
    return <LoadingPage />;
  }

  if (isPatchJobError || isPatchConfigError || isErrorLoadingModel) {
    return (
      <WizardErrorPage
        pageTitle={
          <>
            <FontAwesomeIcon icon={faWandMagicSparkles} /> {i18n('instructions:create.title')}
          </>
        }
        pageSubtitle={i18n('instructions:create.preview.title')}
        errorTitle={i18n('instructions:create.processing.loadFailedError.title')}
        errorMessage={i18n('instructions:create.processing.loadFailedError.message')}
        errorIcon={<FontAwesomeIcon icon={faCloudExclamation} />}
        errorActions={
          <Button linkComponent={Link} to="/instructions">
            <span>
              {i18n('instructions:create.processing.loadFailedError.backToLibrary')} <FontAwesomeIcon icon={faRight} />
            </span>
          </Button>
        }
      />
    );
  }

  return (
    <>
      <div className={styles.modelViewer}>
        <div className={styles.model}>
          {model && !isLoadingModel && !isErrorLoadingModel && (
            <ModelViewer model={model} swapFacing={swapFacing} swapYZAxis={swapYZAxis} />
          )}
        </div>
        <div className={styles.modelActionButtons}>
          <Button variant="secondary" className={styles.modelAction} onClick={() => setSwapYZAxis(!swapYZAxis)}>
            <FontAwesomeIcon icon={faArrowRotateLeft} />
            {i18n('instructions:create.preview.changeVerticalOrientation')}
          </Button>
          <Button variant="secondary" className={styles.modelAction} onClick={() => setSwapFacing(!swapFacing)}>
            <FontAwesomeIcon icon={faArrowRotateLeft} className={styles.customRotate} />
            {i18n('instructions:create.preview.changeFacingDirection')}
          </Button>
        </div>
        <div className={styles.save}>
          <Button onClick={() => setConfirmChanges(true)}>
            {i18n('instructions:create.preview.saveChanges')}
            <FontAwesomeIcon icon={faRight} />
          </Button>
        </div>
      </div>
      <Dialog open={confirmChanges} modal onClose={() => setConfirmChanges(false)}>
        <DialogPanel>
          <DialogHeader>
            <Typography as="p" appearance="heading5" overflow="hidden" textOverflow="ellipsis">
              {i18n('instructions:create.preview.confirmChangesTitle')}
            </Typography>
          </DialogHeader>
          <DialogContent>{i18n('instructions:create.preview.confirmChangesMessage')}</DialogContent>
          <DialogFooter>
            <Button
              data-testid="confirm-delete-product"
              variant="primary"
              onClick={handleSubmit}
              disabled={isPatchConfigLoading || isPatchJobLoading}
            >
              {i18n('instructions:create.preview.confirmSaveChanges')}
            </Button>
            <Button
              variant="tertiary"
              onClick={() => setConfirmChanges(false)}
              disabled={isPatchConfigLoading || isPatchJobLoading}
            >
              {i18n('instructions:create.preview.cancelChanges')}
            </Button>
          </DialogFooter>
        </DialogPanel>
      </Dialog>
    </>
  );
}
